// Petrol Requests: a driver manager asks for petrol for a driver's car, and
// Luyanda approves and pays it by eWallet or into a bank account.
// db/migrations/0950.
//
// A rebate (db/migrations/8001) is the other kind: a station is paid back for
// fuel a driver already poured when the Fuel Easy card was refused. It carries
// the fuel slip, is dated the day on the slip, and cannot be approved or paid
// until the slip is filed.
//
// A paid request is Fleetly's fuel spend for that car and month
// (v_petrol_request_fuel_spend). It is never a loan and never a deduction,
// because the driver contract says fuel never is (12.2.5, 13.6). It is also
// deliberately NOT a row in fuel_card_transactions: the pump receipt the driver
// brings back is read into that table, and writing the request there too would
// count the same litres twice.
//
// Approving and paying are for the releaser only (can_release_loan_payments),
// in two steps or in one, or by emailing the proof with FR-0000 in the subject.
// Every figure and every refusal here is Postgres. CLAUDE.md rule 4.
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Check, ChevronDown, ChevronRight, ExternalLink, Fuel, ReceiptText, ShieldAlert, X } from 'lucide-react'
import {
  useApprovePetrolRequest, useCancelPetrolRequest, useDeclinePetrolRequest, useGetPetrolProofUrl,
  useGetPetrolRequestOptions, useGetPetrolRequests, useMarkPetrolRequestPaid,
} from '../hooks/backend/petrol'
import { Button } from '../lib/shadcn/button'
import { Input } from '../lib/shadcn/input'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Spinner } from '../lib/shadcn/spinner'
import { Chip } from '../components/interviews/Chip'
import { RaisePetrolRequestDialog } from '../components/petrol/RaisePetrolRequestDialog'
import { PaymentFiles } from '../components/payments/PaymentFiles'
import { formatDay, formatWhen, formatZar } from '../utils/loans'
import {
  PETROL_STATUS_LABEL, PETROL_STATUS_TONE, formatMonth,
  type PetrolKind, type PetrolList, type PetrolOptions, type PetrolRequest,
} from '../utils/petrol'

// The mailbox the driver-loans edge function reads for proofs (0361).
const PROOF_ADDRESS = 'admin@fleetly.co.za'

const FILTERS: { value: string; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: '', label: 'All' },
  { value: 'requested', label: 'Waiting for approval' },
  { value: 'approved', label: 'Approved, to pay' },
  { value: 'paid', label: 'Paid' },
  { value: 'declined', label: 'Declined' },
  { value: 'cancelled', label: 'Cancelled' },
]

export default function PetrolRequests() {
  const optionsQuery = useGetPetrolRequestOptions<PetrolOptions | null>()
  const listQuery = useGetPetrolRequests<PetrolList | null>()
  const [params] = useSearchParams()
  // ?r= is the link in the emails, so it opens on that request whatever its status.
  const deepLink = params.get('r')
  const [status, setStatus] = useState(deepLink ? '' : 'open')
  const [driver, setDriver] = useState('')
  const [kind, setKind] = useState('')
  const [expanded, setExpanded] = useState<string | null>(deepLink)
  const [raising, setRaising] = useState<PetrolKind | null>(null)
  const [showSpend, setShowSpend] = useState(true)

  const loadOptions = optionsQuery.trigger
  const loadList = listQuery.trigger
  useEffect(() => { void loadOptions({}, { skipCache: true }).catch(() => undefined) }, [loadOptions])
  const refreshList = useCallback(() => {
    void loadList({ status: status || null, driver_id: driver || null, kind: kind || null }, { skipCache: true }).catch(() => undefined)
  }, [loadList, status, driver, kind])
  useEffect(() => { refreshList() }, [refreshList])

  const options = optionsQuery.data ?? null
  const rows = listQuery.data?.rows ?? []
  const counts = listQuery.data?.counts
  const spend = listQuery.data?.fuel_spend ?? []
  const releasers = options?.releasers ?? 'Luyanda'

  if (optionsQuery.loading && !optionsQuery.data && !optionsQuery.error) {
    return <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading…</div>
  }
  if (optionsQuery.error) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md space-y-3 rounded-lg border border-border bg-card p-6 text-center text-sm">
          <p className="text-rose-700 dark:text-rose-300">{optionsQuery.error}</p>
          <Button size="sm" variant="outline" onClick={() => void loadOptions({}, { skipCache: true }).catch(() => undefined)}>Try again</Button>
        </div>
      </div>
    )
  }

  // Postgres answers null when this login may not see petrol requests at all
  // (petrol_requests_allowed_v1), so say why rather than show an empty list.
  if (optionsQuery.data === null && !optionsQuery.loading) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">Petrol requests are open to driver managers and the people who run pay.</p>
          <p className="text-xs text-muted-foreground">If you should be one of them, ask an admin to switch on Edit drivers for you in cockpit access.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
          <Fuel className="h-5 w-5" />
        </div>
        <div className="min-w-[12rem] flex-1">
          <h1 className="text-lg font-semibold text-foreground">Petrol Requests</h1>
          <p className="text-xs text-muted-foreground">
            Petrol for a driver's shift, or paying back a station when the Fuel Easy card was refused, approved and paid by {releasers}. It is fuel spend for the car, never a loan or a deduction.
          </p>
        </div>
        {options?.can_raise && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setRaising('rebate')}>
              <ReceiptText className="mr-1 h-4 w-4" /> Pay back a station
            </Button>
            <Button variant="brand" size="sm" onClick={() => setRaising('advance')}>
              <Fuel className="mr-1 h-4 w-4" /> Request petrol
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
        {counts && (
          <>
            <Chip tone={counts.to_approve ? 'amber' : 'neutral'}>{counts.to_approve} waiting for approval</Chip>
            <Chip tone={counts.to_pay ? 'brand' : 'neutral'}>{counts.to_pay} approved, to pay</Chip>
            {counts.rebates_open > 0 && <Chip tone="amber">{counts.rebates_open} station rebate{counts.rebates_open === 1 ? '' : 's'} open</Chip>}
            <Chip tone="neutral">{counts.paid_this_month} paid this month, {formatZar(counts.paid_this_month_zar)}</Chip>
          </>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <NativeSelect className="h-8 w-44 text-xs" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
            {FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </NativeSelect>
          <NativeSelect className="h-8 w-40 text-xs" value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Kind">
            <option value="">Both kinds</option>
            <option value="advance">Petrol for a shift</option>
            <option value="rebate">Station rebates</option>
          </NativeSelect>
          <NativeSelect className="h-8 w-48 text-xs" value={driver} onChange={(e) => setDriver(e.target.value)} aria-label="Driver">
            <option value="">Every driver</option>
            {(options?.drivers ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </NativeSelect>
        </div>
      </div>

      {listQuery.error && <div className="border-b border-border bg-rose-50 px-4 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">{listQuery.error}</div>}

      <div className="min-h-0 flex-1 overflow-y-auto">
        <section className="border-b border-border bg-muted/20">
          <button type="button" onClick={() => setShowSpend((v) => !v)} className="flex w-full items-center gap-2 px-4 py-2 text-left text-xs font-medium text-foreground">
            {showSpend ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            Fuel spend from paid petrol requests, by car, last three months
          </button>
          {showSpend && (
            <div className="px-4 pb-3">
              {spend.length === 0 ? (
                <p className="text-xs text-muted-foreground">No petrol request has been paid in the last three months.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[18rem] text-xs">
                    <thead className="text-left text-muted-foreground">
                      <tr><th className="py-1 pr-3 font-medium">Month</th><th className="py-1 pr-3 font-medium">Car</th><th className="py-1 pr-3 text-right font-medium">Requests</th><th className="py-1 text-right font-medium">Paid</th></tr>
                    </thead>
                    <tbody>
                      {spend.map((s) => (
                        <tr key={`${s.month}-${s.registration}`} className="border-t border-border">
                          <td className="py-1 pr-3">{formatMonth(s.month)}</td>
                          <td className="py-1 pr-3 font-mono">{s.registration}</td>
                          <td className="py-1 pr-3 text-right tabular-nums">{s.requests}</td>
                          <td className="py-1 text-right font-medium tabular-nums">{formatZar(s.amount_zar)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>

        {listQuery.loading && !listQuery.data ? (
          <p className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading petrol requests…</p>
        ) : rows.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            {status === 'open' && !driver && !kind ? 'Nothing is waiting. Request petrol or pay back a station with the buttons above.' : 'No petrol request matches these filters.'}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((r) => (
              <RequestItem
                key={r.id}
                req={r}
                open={expanded === r.id}
                onToggle={() => setExpanded((x) => (x === r.id ? null : r.id))}
                canRelease={options?.can_release === true}
                canRaise={options?.can_raise === true}
                releasers={releasers}
                onChanged={refreshList}
              />
            ))}
          </ul>
        )}
      </div>

      <RaisePetrolRequestDialog
        open={raising !== null}
        kind={raising ?? 'advance'}
        onOpenChange={(o) => { if (!o) setRaising(null) }}
        options={options}
        onSaved={refreshList}
      />
    </div>
  )
}

function RequestItem({ req: r, open, onToggle, canRelease, canRaise, releasers, onChanged }: {
  req: PetrolRequest; open: boolean; onToggle: () => void; canRelease: boolean; canRaise: boolean
  releasers: string; onChanged: () => void
}) {
  const approve = useApprovePetrolRequest()
  const decline = useDeclinePetrolRequest()
  const paid = useMarkPetrolRequestPaid()
  const cancel = useCancelPetrolRequest()
  const proof = useGetPetrolProofUrl<{ url: string }>()
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [declining, setDeclining] = useState(false)
  const [declineReason, setDeclineReason] = useState('')
  const [cancelling, setCancelling] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const busy = approve.loading || decline.loading || paid.loading || cancel.loading

  const run = async (fn: () => Promise<unknown>) => {
    setError(null)
    try { await fn(); onChanged() } catch (e) { setError(e instanceof Error ? e.message : String(e)) }
  }

  // Opened before the await so the browser treats it as the click's own window.
  async function openProof(id: string) {
    setError(null)
    const w = window.open('', '_blank')
    try {
      const res = await proof.trigger({ id }, { skipCache: true })
      if (res?.url && w) w.location.href = res.url
      else w?.close()
    } catch (e) {
      w?.close()
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  const unpaid = r.status === 'requested' || r.status === 'approved'
  const rebate = r.kind === 'rebate'
  // Postgres refuses to approve or pay a rebate without its slip; the buttons
  // say so before anyone presses them.
  const slipMissing = rebate && !r.has_receipt

  return (
    <li>
      <button type="button" onClick={onToggle} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left hover:bg-accent/40">
        {open ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
        <span className="w-16 shrink-0 font-mono text-xs text-muted-foreground">{r.ref}</span>
        <span className="min-w-[10rem] flex-1">
          <span className="block text-sm font-medium text-foreground">{r.driver_name}</span>
          <span className="block text-xs text-muted-foreground">
            {r.vehicle ?? '—'}, {rebate ? `${r.station_name ?? 'station'}, slip of ${formatDay(r.for_date)}` : `shift of ${formatDay(r.for_date)}`}
          </span>
        </span>
        {rebate && <Chip tone={slipMissing ? 'amber' : 'neutral'}>{slipMissing ? 'Rebate, no slip yet' : 'Rebate'}</Chip>}
        <span className="w-24 text-right text-sm font-semibold tabular-nums text-foreground">{formatZar(r.amount_zar)}</span>
        <span className="flex w-full justify-end sm:w-auto">
          <Chip tone={PETROL_STATUS_TONE[r.status]}>{PETROL_STATUS_LABEL[r.status]}</Chip>
        </span>
      </button>

      {open && (
        <div className="space-y-3 bg-muted/20 px-4 pb-4 pl-11 text-sm">
          <dl className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
            {rebate && <Row k="Kind" v="Paying back a station: the Fuel Easy card was refused" />}
            {rebate && <Row k="Station" v={r.station_name ?? '—'} />}
            <Row k={rebate ? 'What happened' : 'Why it is needed'} v={r.reason} />
            <Row k="Pay by" v={r.payout_text} />
            <Row k="Car" v={r.vehicle ?? '—'} />
            <Row k={rebate ? 'Day on the slip' : 'Shift date'} v={formatDay(r.for_date)} />
            <Row k="Raised by" v={`${r.requested_by_name ?? '—'}, ${formatWhen(r.requested_at)}`} />
            {r.notified_at && <Row k="Sent for approval" v={`${releasers} emailed ${formatWhen(r.notified_at)}`} />}
            {r.approved_at && <Row k="Approved" v={`${formatWhen(r.approved_at)}${r.approved_by_name ? ` by ${r.approved_by_name}` : ''}`} />}
            {r.paid_out_at && <Row k="Paid" v={`${formatWhen(r.paid_out_at)} by ${r.paid_out_by_name ?? '—'}, ${r.paid_out_method === 'emailed_proof' ? 'proof of payment emailed' : 'marked paid'}${r.paid_out_note && r.paid_out_method !== 'emailed_proof' ? `: ${r.paid_out_note}` : ''}`} />}
            {r.declined_at && <Row k="Declined" v={`${formatWhen(r.declined_at)}: ${r.decline_reason ?? ''}`} />}
            {r.cancelled_at && <Row k="Cancelled" v={`${formatWhen(r.cancelled_at)}: ${r.cancel_reason ?? ''}`} />}
          </dl>

          {unpaid && canRelease && (
            <div className="space-y-2 rounded-md border border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] p-3 text-xs text-[hsl(var(--brand-soft-foreground))]">
              <p className="text-sm font-semibold">Pay {formatZar(r.amount_zar)} by {r.payout_text}.</p>
              {slipMissing && <p className="font-medium">The fuel slip is not filed yet, so this rebate cannot be approved or paid. {r.requested_by_name ?? 'The manager'} uploads it below.</p>}
              {!slipMissing && (
                <p>
                  When it is paid, press the button, or email the proof of payment to {PROOF_ADDRESS} with {r.ref} in the subject. Replying to the email you were sent does exactly that, and approves it at the same time.
                </p>
              )}
              <span className="flex flex-wrap items-center gap-1">
                <Input className="h-8 w-60 bg-background text-xs" placeholder="Optional note, e.g. eWallet from FNB" value={note} onChange={(e) => setNote(e.target.value)} />
                <Button size="sm" variant="brand" disabled={busy || slipMissing} onClick={() => void run(() => paid.trigger({ id: r.id, note }))}>
                  {paid.loading ? <Spinner className="mr-1 h-3.5 w-3.5" /> : <Check className="mr-1 h-3.5 w-3.5" />}
                  {r.status === 'requested' ? 'Approve and mark paid' : 'I have paid'}
                </Button>
                {r.status === 'requested' && (
                  <Button size="sm" variant="outline" className="bg-background" disabled={busy || slipMissing} onClick={() => void run(() => approve.trigger({ id: r.id }))}>
                    Approve, pay later
                  </Button>
                )}
                {!declining && (
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => setDeclining(true)}>Decline</Button>
                )}
              </span>
              {declining && (
                <span className="flex flex-wrap items-center gap-1">
                  <Input className="h-8 w-64 bg-background text-xs" placeholder="Why it is declined (the manager reads this)" value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} />
                  <Button size="sm" variant="destructive" disabled={busy}
                    onClick={() => void run(async () => { await decline.trigger({ id: r.id, reason: declineReason }); setDeclining(false) })}>
                    Decline it
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setDeclining(false)}>Back</Button>
                </span>
              )}
            </div>
          )}
          {unpaid && !canRelease && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-100">
              {slipMissing
                ? `Upload the fuel slip below. It goes to ${releasers} for approval once the slip is filed.`
                : r.status === 'requested' ? `Waiting for ${releasers} to approve it.` : `Approved. Waiting for ${releasers} to pay it.`}
              {!rebate && ' Do not send the driver to the pump until it shows paid.'}
            </p>
          )}
          {r.status === 'paid' && !rebate && (
            <p className="text-xs text-muted-foreground">Ask the driver for the fuel slip. It is read like any other receipt.</p>
          )}

          {(r.files.length > 0 || canRaise || canRelease) && (
            <div className="space-y-1">
              <span className="text-xs font-medium text-foreground">Files</span>
              {r.files.length === 0 && <p className="text-xs text-muted-foreground">{rebate ? 'No fuel slip yet.' : 'No files on this request.'}</p>}
              <PaymentFiles
                kind="petrol"
                requestId={r.id}
                files={r.files}
                uploadRoles={[
                  ...(rebate && (canRaise || canRelease) && r.status !== 'declined' && r.status !== 'cancelled'
                    ? [{ role: 'receipt' as const, label: r.has_receipt ? 'Add another slip' : 'Upload the fuel slip' }] : []),
                  ...(canRelease ? [{ role: 'proof' as const, label: 'Upload proof of payment' }] : []),
                ]}
                onChanged={onChanged}
              />
            </div>
          )}

          {r.proofs.length > 0 && (
            <div className="space-y-1">
              <span className="text-xs font-medium text-foreground">Proof of payment</span>
              <ul className="space-y-1">
                {r.proofs.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center gap-2 text-xs">
                    <Button size="sm" variant="outline" className="h-7" disabled={proof.loading} onClick={() => void openProof(p.id)}>
                      <ExternalLink className="mr-1 h-3.5 w-3.5" /> {p.filename ?? 'Attachment'}
                    </Button>
                    <span className="text-muted-foreground">emailed {formatWhen(p.received_at)}{p.from_email ? ` by ${p.from_email}` : ''}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {unpaid && (canRaise || canRelease) && (
            <div className="flex flex-wrap items-center gap-2">
              {!cancelling ? (
                <Button size="sm" variant="ghost" onClick={() => setCancelling(true)}>Cancel this request</Button>
              ) : (
                <span className="flex flex-wrap items-center gap-1">
                  <Input className="h-8 w-64 text-xs" placeholder="Why it is being cancelled" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
                  <Button size="sm" variant="destructive" disabled={busy}
                    onClick={() => void run(async () => { await cancel.trigger({ id: r.id, reason: cancelReason }); setCancelling(false) })}>
                    Cancel it
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setCancelling(false)}><X className="h-3.5 w-3.5" /></Button>
                </span>
              )}
            </div>
          )}
          {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">{error}</p>}
        </div>
      )}
    </li>
  )
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex gap-2">
      <dt className="w-36 shrink-0 text-muted-foreground">{k}</dt>
      <dd className="min-w-0 break-words text-foreground">{v}</dd>
    </div>
  )
}
